<template>
  <div class="add_new_inner wrap-surg-add">
    <div class="subtitle center no_mobile">סיכומים ומסמכים לקראת ניתוח</div>
    <h2 class="subtitle_inner mobile_only">סיכומים ומסמכים לקראת ניתוח:</h2>

    <ValidationObserver ref="documentsForm">
      <form @submit.prevent="onSubmit" name="documentsForm">
        <div id="files-container">
          <template v-for="(fileRow, rowIndex) in formData.files">
            <div
              v-if="shouldShowFileRow(fileRow.type)"
              :id="'file_' + fileRow.type"
              :key="fileRow.title"
              :ref="'fileRow_' + fileRow.type"
              class="book_surgery toggleContainer desk_inner_width"
              :class="[
                'intro-' + fileRow.type,
                { active: expandedSections[rowIndex] }
              ]"
            >
              <a class="book_surgery_top flex" @click="toggleSection(rowIndex)">
                <span
                  class="title"
                  :class="{
                    'flex is--fit desk-flex--between': fileRow.title.includes(
                      '*'
                    )
                  }"
                >
                  {{ fileRow.title }}
                  (ניתן להוסיף מסמך בסיומת pdf, jpg, png)

                  <Tags
                    v-show="fileRow.title.includes('*')"
                    :isRequired="true"
                  />
                </span>
                <span class="plus-wrap flex m-flex--center">
                  <span class="plus"></span>
                </span>
              </a>

              <div
                class="book_surgery_open toggleInner"
                :style="{
                  display: expandedSections[rowIndex] ? 'block' : 'none'
                }"
              >
                <div
                  class="inner_group bg--white desk-no-pad desk-flex desk-flex--top desk-flex--start surg-doc-container"
                >
                  <!-- Confirm Agreement Select -->
                  <div
                    v-if="fileRow.type === 'clinic-confirm-agreement'"
                    class="add_new_row flex file_row"
                    style="padding: 0"
                  >
                    <div class="add_new_row_col1"></div>
                    <div class="add_new_row_col2">
                      <div class="input-opacity desk-block">
                        <validation-provider
                          ref="confirm_agreement"
                          name="confirm_agreement"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <select2
                            dir="rtl"
                            v-model="formData.confirm_agreement"
                          >
                            <option value="">בחר</option>
                            <option :value="true">
                              המטופל חתם במרפאתי על הסכמה מדעת לפעולה
                            </option>
                            <option :value="false">
                              המטופל יחתום בבית החולים על הסכמה מדעת לפעולה
                            </option>
                          </select2>
                          <span class="error-message">{{ errors[0] }}</span>
                        </validation-provider>
                      </div>
                    </div>
                  </div>

                  <!-- File Upload Section -->
                  <validation-provider
                    v-if="shouldShowFileUpload(fileRow)"
                    :ref="'file_' + fileRow.type"
                    :name="'file_' + fileRow.type"
                    :rules="fileRow.title.includes('*') ? 'required' : ''"
                    v-slot="{ errors }"
                  >
                    <div
                      class="add_new_row_docs m-pt-15 flex flex--start svg-block"
                    >
                      <input
                        :for="'file_' + fileRow.type"
                        type="text"
                        v-model="fileRow.files"
                        v-show="false"
                      />
                      <a
                        v-for="(file, index) in fileRow.files"
                        :key="index"
                        class="file_item relative flex desk-block"
                        href="#"
                        @click.prevent="downloadDocument(file.path, file.name)"
                      >
                        <span
                          class="file-remove"
                          @click.stop="removeFile(rowIndex, index)"
                        >
                          <CloseSvg />
                        </span>
                        <img
                          class="no_mobile"
                          src="/assets/img/svg/ic-file.svg"
                        />
                        <span class="tx_file block">{{ file.name }}</span>
                      </a>
                      <span
                        class="bt_upload relative flex desk-block"
                        @click="addDocuments(fileRow.type)"
                      >
                        <input type="text" />
                        <div class="ic-upload ease">
                          <UploadSvg />
                        </div>
                        <div class="tx_file ease">הוספת מסמך</div>
                      </span>
                    </div>
                    <span class="error-message">{{ errors[0] }}</span>
                  </validation-provider>

                  <!-- Note (mobile) -->
                  <span
                    v-show="fileRow.note && shouldShowFileUpload(fileRow)"
                    class="text--20 mobile_only text--error tx--red"
                  >
                    {{ fileRow.note }}
                  </span>
                </div>

                <!-- Note (desktop) -->
                <span
                  v-show="fileRow.note && shouldShowFileUpload(fileRow)"
                  class="text--20 no_mobile text--error tx--red"
                >
                  {{ fileRow.note }}
                </span>
              </div>
            </div>
          </template>
        </div>

        <div class="surg-bottom-actions center">
          <button type="submit" class="bt_blue">הבא</button>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex";
import UploadSvg from "@/assets/img/svg/upload.svg";
import AddDocuments from "@/components/Popups/AddDocuments";
import CloseSvg from "@/assets/img/svg/close.svg";
import Tags from "@/components/Dashboard/Surgery/AddSurgery/Steps/SurgeryDetails/modules/Tags.vue";
import {
  downloadDocument,
  getSurgeryDocuments
} from "@/utilities/download-document";

const HEADER_OFFSET = 120;

const IMAGING_DIAGNOSIS_CODES = [
  "24",
  "25",
  "37",
  "38",
  "130",
  "262",
  "336",
  "414",
  "349",
  "344"
];

const DEFAULT_FILES = [
  {
    type: "clinic-confirm-agreement",
    files: [],
    title: "הסכמה מדעת לפעולה*",
    note: `לתשומת ליבך, ע"פ הנחיות משרד הבריאות, מסמך הסכמה מדעת בתוקף עד 30 יום מיום חתימת המטופל.`
  },
  {
    type: "reference-document",
    files: [],
    title: "סיכום ייעוץ לקראת ניתוח*",
    note:
      "*לאור דרישות קופות החולים וחברות הביטוח, יש לצרף מסמך סיכום ייעוץ המפנה לניתוח."
  },
  {
    type: "ext-imaging-diagnosis",
    files: [],
    title: "פענוח בדיקות הדמייה*",
    note: `ע"פ נהלי הקופה, קוד הניתוח הנבחר מחייב העלאת פענוח בדיקת הדמייה.`
  },
  { type: "", files: [], title: "קבצים נוספים" }
];

export default {
  name: "Documents",
  components: { UploadSvg, CloseSvg, Tags },
  data() {
    return {
      formData: {
        files: JSON.parse(JSON.stringify(DEFAULT_FILES)),
        confirm_agreement: ""
      },
      expandedSections: [true, true, true, true] // All sections expanded by default
    };
  },
  computed: {
    ...mapState("Surgery", [
      "surgery",
      "isEditMode",
      "insuranceSurgeryTemp",
      "insuranceForm"
    ]),
    ...mapGetters("Surgery", ["isPayTypeCareService", "showImplantsSection"]),
    insuranceType() {
      return this.isPayTypeCareService
        ? this.insuranceForm.payType
        : this.surgery.insurance_type;
    },
    showResultsFile() {
      if (this.insuranceType !== "כללית מושלם") return false;

      return this.insuranceSurgeryTemp.some(
        i => i.code && IMAGING_DIAGNOSIS_CODES.includes(i.code.split(" > ")[0])
      );
    },
    surgeryId() {
      return this.isEditMode
        ? this.$route.params.id
        : sessionStorage.getItem("temp_surgery");
    }
  },
  created() {
    // Initialize formData from store
    this.formData.confirm_agreement = this.surgery.confirm_agreement ?? "";

    if (this.surgery.files?.length) {
      this.formData.files = JSON.parse(JSON.stringify(this.surgery.files));
    }
  },
  mounted() {
    // Listen for uploaded documents
    this.$root.$on("uploadSurgFiles", this.handleUploadedFiles);
  },
  beforeDestroy() {
    this.$root.$off("uploadSurgFiles", this.handleUploadedFiles);
  },
  methods: {
    ...mapMutations("Surgery", ["SET_SURGERY", "SET_CURRENT_STEP"]),
    downloadDocument,
    toggleSection(index) {
      this.$set(this.expandedSections, index, !this.expandedSections[index]);
    },
    shouldShowFileRow(type) {
      if (type === "ext-imaging-diagnosis") return this.showResultsFile;
      return true;
    },
    shouldShowFileUpload(fileRow) {
      if (fileRow.type !== "clinic-confirm-agreement") return true;
      // select2 may return string or boolean
      return (
        this.formData.confirm_agreement === true ||
        this.formData.confirm_agreement === "true"
      );
    },
    addDocuments(type = null) {
      this.$modal.show(AddDocuments, {
        chosenType: type,
        directUpload: { id: this.surgeryId, type: "surgery" }
      });
    },
    removeFile(rowIndex, fileIndex) {
      const file = this.formData.files[rowIndex].files[fileIndex];
      this.$API.uploadFile(
        this.surgeryId,
        "cancel",
        file.id,
        file.name,
        [],
        true,
        this.formData.files[rowIndex].type
      );

      this.formData.files[rowIndex].files.splice(fileIndex, 1);
    },
    async handleUploadedFiles() {
      const resDoc = await getSurgeryDocuments(this.surgeryId);
      resDoc.forEach((doc, i) => {
        if (this.formData.files[i]) {
          this.formData.files[i].files = doc.files;
        }
      });
    },
    async onSubmit() {
      const isValid = await this.$refs.documentsForm.validate();

      if (!isValid) {
        this.scrollToFirstError();
        return;
      }

      this.SET_SURGERY({
        files: this.formData.files,
        confirm_agreement: this.formData.confirm_agreement
      });
      this.SET_CURRENT_STEP(this.showImplantsSection ? 7 : 6);
    },
    scrollToFirstError() {
      const errors = this.$refs.documentsForm.errors;

      for (const fieldName in errors) {
        if (errors[fieldName].length > 0) {
          const ref = this.$refs[fieldName];
          const element = Array.isArray(ref) ? ref[0]?.$el : ref?.$el;

          if (element) {
            const elementTop =
              element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top: elementTop - HEADER_OFFSET,
              behavior: "smooth"
            });
          }
          break;
        }
      }
    }
  }
};
</script>
